import * as React from 'react';

import { MultiSelectFilter } from '@rematter/ui-web';
import { cn } from '@rematter/ui-web/lib';
import { useAtom } from 'jotai';

import { $columnSelectedSupplierIds } from '../../../_state/brokeredLoadsList.state';
import { useTranslations } from '../../../i18n/useTranslations';

import {
  SelectableSupplierAccount,
  useSupplierAccountsQuery,
} from '~/shared/components/select-fields/SelectSupplierAccount';

interface SupplierColumnFilterProps {
  onClose?: () => void;
}

export const SupplierColumnFilter: React.FC<SupplierColumnFilterProps> = ({
  onClose,
}) => {
  const t = useTranslations();
  const [selectedSupplierIdsSet, setSelectedSupplierIdsSet] = useAtom(
    $columnSelectedSupplierIds,
  );
  const selectedSupplierIds = Array.from(selectedSupplierIdsSet);
  const [searchTerm, setSearchTerm] = React.useState<string | undefined>(
    undefined,
  );

  const selectedKeys = React.useMemo(
    () => new Set<React.Key>(selectedSupplierIds),
    [selectedSupplierIds],
  );

  const {
    data: suppliers,
    isFetching: isFetchingSuppliers,
    isLoading: isLoadingSuppliers,
  } = useSupplierAccountsQuery({
    searchTerm,
  });

  const { data: selectedSuppliers } = useSupplierAccountsQuery({
    ids: selectedSupplierIds,
  });

  const handleSearch = React.useCallback((query: string) => {
    setSearchTerm(query);
  }, []);

  const handleSelectionChange = React.useCallback(
    (keys: Set<React.Key>) => {
      setSelectedSupplierIdsSet(new Set(Array.from(keys) as string[]));
    },
    [setSelectedSupplierIdsSet],
  );

  const handleReset = React.useCallback(() => {
    setSelectedSupplierIdsSet(new Set());
  }, [setSelectedSupplierIdsSet]);

  const getLabel = React.useCallback(
    (item: SelectableSupplierAccount, isSelected: boolean) => (
      <div className="flex w-full items-center gap-2">
        <div
          className={cn(
            'w-0 grow truncate text-sm',
            isSelected ? 'text-neutral' : 'text-neutral/60',
          )}
        >
          {item.name}
        </div>
        {item.type && (
          <span
            className={cn(
              'rounded px-1.5 py-0.5 text-xs',
              item.type === 'Individual'
                ? 'bg-warning/10 text-warning'
                : 'bg-primary/10 text-primary',
            )}
          >
            {item.type}
          </span>
        )}
      </div>
    ),
    [],
  );

  return (
    <MultiSelectFilter
      className="@SupplierColumnFilter min-w-[350px]"
      entries={suppliers}
      selectedEntries={selectedSuppliers}
      selectedKeys={selectedKeys}
      onSelectionChange={handleSelectionChange}
      getEntryKey={(supplier) => supplier.id}
      getEntryLabel={getLabel}
      onSearch={handleSearch}
      onDismiss={onClose}
      onReset={handleReset}
      searchPlaceholder={
        t.brokerage.loads.columnFilters.supplier.searchPlaceholder
      }
      isLoading={isLoadingSuppliers}
      isFetching={isFetchingSuppliers}
    />
  );
};
